import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusCircle } from 'lucide-react';
import { GuideSection } from '../components/GuideSection';
import { GuideSubSection } from '../components/GuideSubSection';
import { RoleHint } from '../components/RoleHint';

/** One numbered step inside a ticket-type walkthrough. */
function Step({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">{title}</h4>
      {children}
    </div>
  );
}

export function CreatingTicketsSection() {
  const { t } = useTranslation('help');
  const s = 'sections.creatingTickets';

  return (
    <GuideSection
      id="creating-tickets"
      title={t(`${s}.title`)}
      description={t(`${s}.description`)}
      icon={PlusCircle}
    >
      <GuideSubSection title={t(`${s}.overview.title`)}>
        <p>{t(`${s}.overview.p1`)}</p>
        <p>{t(`${s}.overview.p2`)}</p>
        <p className="text-xs text-slate-500 italic">{t(`${s}.overview.permissionsHint`)}</p>
      </GuideSubSection>

      <GuideSubSection title={t(`${s}.similarTickets.title`)}>
        <p>{t(`${s}.similarTickets.p1`)}</p>
        <p>{t(`${s}.similarTickets.p2`)}</p>
      </GuideSubSection>

      {/* Order Management */}
      <GuideSubSection title={t(`${s}.orderManagement.title`)} collapsible defaultOpen>
        <div className="space-y-4">
          <p>{t(`${s}.orderManagement.intro`)}</p>

          <Step title={t(`${s}.orderManagement.step1Title`)}>
            <p>{t(`${s}.orderManagement.step1Intro`)}</p>
            <ul className="list-disc list-inside space-y-1 ml-1 mt-2">
              <li>{t(`${s}.orderManagement.step1General`)}</li>
              <li>{t(`${s}.orderManagement.step1Distance`)}</li>
              <li>{t(`${s}.orderManagement.step1Checked`)}</li>
              <li>{t(`${s}.orderManagement.step1Others`)}</li>
            </ul>
          </Step>

          <Step title={t(`${s}.orderManagement.step2Title`)}>
            <p>{t(`${s}.orderManagement.step2Intro`)}</p>
            <ul className="list-disc list-inside space-y-1 ml-1 mt-2">
              <li>{t(`${s}.orderManagement.step2Jvid`)}</li>
              <li>{t(`${s}.orderManagement.step2AddressSearch`)}</li>
              <li>{t(`${s}.orderManagement.step2Utac`)}</li>
            </ul>
            <p className="mt-2">{t(`${s}.orderManagement.step2Preview`)}</p>
            <p className="text-xs text-slate-500 italic mt-2">{t(`${s}.orderManagement.step2NotFound`)}</p>
          </Step>

          <Step title={t(`${s}.orderManagement.step3Title`)}>
            <p>{t(`${s}.orderManagement.step3`)}</p>
          </Step>

          <Step title={t(`${s}.orderManagement.step4Title`)}>
            <p>{t(`${s}.orderManagement.step4`)}</p>
          </Step>
        </div>
      </GuideSubSection>

      {/* Address Management */}
      <GuideSubSection title={t(`${s}.addressManagement.title`)} collapsible>
        <div className="space-y-4">
          <p>{t(`${s}.addressManagement.intro`)}</p>

          <Step title={t(`${s}.addressManagement.step1Title`)}>
            <p>{t(`${s}.addressManagement.step1Intro`)}</p>
            <ul className="list-disc list-inside space-y-1 ml-1 mt-2">
              <li>{t(`${s}.addressManagement.step1Source`)}</li>
              <li>{t(`${s}.addressManagement.step1Action`)}</li>
              <li>{t(`${s}.addressManagement.step1Status`)}</li>
              <li>{t(`${s}.addressManagement.step1Scope`)}</li>
            </ul>
          </Step>

          <Step title={t(`${s}.addressManagement.step2Title`)}>
            <p>{t(`${s}.addressManagement.step2Intro`)}</p>
            <ul className="list-disc list-inside space-y-1 ml-1 mt-2">
              <li>{t(`${s}.addressManagement.step2Creation`)}</li>
              <li>{t(`${s}.addressManagement.step2InfoMod`)}</li>
            </ul>
            <p className="mt-2">{t(`${s}.addressManagement.step2BuildingGroup`)}</p>
            <p className="mt-1">{t(`${s}.addressManagement.step2Fields`)}</p>
            <p className="mt-1">{t(`${s}.addressManagement.step2Reference`)}</p>
            <p className="text-xs text-slate-500 italic mt-2">{t(`${s}.addressManagement.step2Mapping`)}</p>
          </Step>

          <Step title={t(`${s}.addressManagement.step3Title`)}>
            <p>{t(`${s}.addressManagement.step3`)}</p>
          </Step>

          <Step title={t(`${s}.addressManagement.step4Title`)}>
            <p>{t(`${s}.addressManagement.step4`)}</p>
          </Step>
        </div>
      </GuideSubSection>

      {/* Support Request */}
      <GuideSubSection title={t(`${s}.supportRequest.title`)} collapsible>
        <div className="space-y-4">
          <p>{t(`${s}.supportRequest.intro`)}</p>

          <Step title={t(`${s}.supportRequest.step1Title`)}>
            <p>{t(`${s}.supportRequest.step1Intro`)}</p>
            <ul className="list-disc list-inside space-y-1 ml-1 mt-2">
              <li>{t(`${s}.supportRequest.step1Category`)}</li>
              <li>{t(`${s}.supportRequest.step1Subject`)}</li>
              <li>{t(`${s}.supportRequest.step1Description`)}</li>
              <li>{t(`${s}.supportRequest.step1Priority`)}</li>
              <li>{t(`${s}.supportRequest.step1StepsToReproduce`)}</li>
              <li>{t(`${s}.supportRequest.step1Attachments`)}</li>
            </ul>
            <p className="text-xs text-slate-500 italic mt-2">{t(`${s}.supportRequest.step1Browser`)}</p>
          </Step>

          <Step title={t(`${s}.supportRequest.step2Title`)}>
            <p>{t(`${s}.supportRequest.step2`)}</p>
          </Step>
        </div>
      </GuideSubSection>

      {/* Build Management */}
      <GuideSubSection title={t(`${s}.buildManagement.title`)} collapsible>
        <div className="space-y-4">
          <p>{t(`${s}.buildManagement.intro`)}</p>
          <RoleHint variant="external">{t(`${s}.buildManagement.availability`)}</RoleHint>

          <Step title={t(`${s}.buildManagement.step1Title`)}>
            <p>{t(`${s}.buildManagement.step1`)}</p>
          </Step>

          <Step title={t(`${s}.buildManagement.step2Title`)}>
            <p>{t(`${s}.buildManagement.step2Area`)}</p>
            <p className="mt-1">{t(`${s}.buildManagement.step2Snapshot`)}</p>
            <p className="mt-1">{t(`${s}.buildManagement.step2Details`)}</p>
            <p className="mt-1">{t(`${s}.buildManagement.step2Jvids`)}</p>
          </Step>

          <Step title={t(`${s}.buildManagement.step3Title`)}>
            <p>{t(`${s}.buildManagement.step3`)}</p>
          </Step>
        </div>
      </GuideSubSection>
    </GuideSection>
  );
}
